import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useContext, useRef
} from 'react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useSgHandleSubQuestions, SgSessionStorageService } from 'saga-modules';
import { TranslatorContext } from '@jutro/locale';
import { WizardContext } from 'gw-portals-wizard-react';
import CommonConstants from 'sg-common-constants';
import SgSelect from 'sg-select';
import { SgDate, SgDateInputTypes } from 'sg-date';
import SpmUtils from 'sg-spm-utils';
import _ from 'lodash';
import moment from 'moment';
import { events } from 'gw-platform-events-js';
import metadata from './SgSpmConvictionForm.metadata.json5';

function SgSpmConvictionForm(props) {
    const {
        id,
        modelVM,
        path,
        showErrors,
        updateData,
        onValidate: onValidateParent,
        onSaveConviction: onSaveConvictionParent,
        onCancelConviction,
        messageParams
    } = props;

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const wizardContext = useContext(WizardContext);
    const [invalidSubQuestions, updateInvalidSubQuestions] = useState(
        SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS)
    );
    const hasSavedConviction = useRef(false);

    const convictionsTypes = useMemo(() => {
        const TOP_CONVICTIONS_IDS = ['dr10', 'in10', 'sp30', 'sp50', 'ts10'];
        const topConvictions = [];
        const allClaims = [];

        _.get(modelVM, `${path}.type.aspects.availableValues`).forEach((val) => {
            const claim = {
                code: val.code,
                name: translator({ id: val.name, defaultMessage: '' })
            };
            if (TOP_CONVICTIONS_IDS.includes(claim.code)) {
                topConvictions.push(claim);
            }
            allClaims.push(claim);
        });

        return [
            {
                id: 'placeholder',
                sectionName: '',
                values: [
                    {
                        id: 'placeholderText',
                        name: translator({ id: 'spm.components.convictionDetails.pleaseSelect', defaultMessage: '' }),
                        code: ''
                    }
                ]
            },
            {
                id: 'topValues',
                sectionName: translator({ id: 'spm.components.convictionDetails.CommonConvictions', defaultMessage: '' }),
                values: topConvictions
            },
            {
                id: 'allClaims',
                sectionName: translator({ id: 'spm.components.convictionDetails.AllConvictions', defaultMessage: '' }),
                values: allClaims
            }
        ];
    }, [modelVM, path, translator]);

    const selectedConviction = useMemo(() => {
        return _.get(modelVM, path);
    }, [modelVM, path]);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        return () => {
            setTimeout(() => {
                if (!window.location.hash.includes(wizardContext.currentStep.path)
                    && !hasSavedConviction.current && !selectedConviction.aspects.subtreeValid && onCancelConviction) { // only change on change of location
                    onCancelConviction();
                    const driverPath = path.split(']')[0];
                    if (_.get(modelVM, driverPath).convictions.length === 0) {
                        _.get(modelVM, driverPath).hasMotorConvictions.value = undefined;
                        updateData(modelVM);
                    }
                }
            });
        };
    }, [selectedConviction]);

    useEffect(() => {
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSaveConviction = useCallback(() => {
        if (!isComponentValid) {
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            return;
        }
        hasSavedConviction.current = true;
        onSaveConvictionParent();
    }, [isComponentValid, onSaveConvictionParent]);

    const calculateConvictionDayToSet = useCallback((dateDto) => {
        return SpmUtils.calculateClaimAndConvictionDay(dateDto, modelVM, 'baseData.periodStartDate.value');
    }, [modelVM]);

    useSgHandleSubQuestions({
        updateModel: updateData,
        updateInvalidSubQuestions,
        modelVM,
        paths: [
            `${path}.convictionDate`,
            `${path}.type`,
            `${path}.wasLicenseSuspended`,
            `${path}.monthsOfSuspension`
        ]
    });

    const overrides = {
        convictionType: {
            path: `${path}.type`,
            values: convictionsTypes,
            onValidate: onValidate,
            value: _.get(modelVM, `${path}.type.value`),
            validationMessages: [
                _.get(modelVM, `${path}.type.aspects.validationMessage`)
            ],
            modelVM,
            updateData,
            showErrors: invalidSubQuestions.includes(`${path}.type`)
        },
        convictionDate: {
            path: `${path}.convictionDate`,
            onValidate: onValidate,
            updateData,
            modelVM,
            minDate: moment().add(-5, 'years').toDate(),
            maxDate: new Date(),
            calculateDayToSet: calculateConvictionDayToSet,
            datePickerTypeDesktop: SgDateInputTypes.MONTH_YEAR_PICKER,
            datePickerTypeMobile: SgDateInputTypes.MONTH_YEAR_INPUTS,
            showErrors: invalidSubQuestions.includes(`${path}.convictionDate`)
        },
        licenseSuspended: {
            path: `${path}.wasLicenseSuspended`,
            label: translator({ id: 'spm.components.convictionDetails.HasLicenseSuspendedLabel', defaultMessage: '' }, messageParams),
            showErrors: invalidSubQuestions.includes(`${path}.wasLicenseSuspended`)
        },
        monthsOfSuspension: {
            visible: _.get(modelVM, `${path}.wasLicenseSuspended.value`) === true,
            path: `${path}.monthsOfSuspension`,
            label: translator({ id: 'spm.components.convictionDetails.MonthsOfSuspensionLabel', defaultMessage: '' }, messageParams),
            showErrors: invalidSubQuestions.includes(`${path}.monthsOfSuspension`)
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            onSaveConviction: onSaveConviction,
            onCancelConviction: onCancelConviction
        },
        resolveComponentMap: {
            SgDate: SgDate,
            SgSelect: SgSelect
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrides}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SgSpmConvictionForm;
